'use client';
import { useEffect, useMemo } from 'react';
import {
  ArrowLeft,
  Hourglass,
  Check,
  X,
  Target,
  Timer,
  Flame,
  Shapes,
  TrendingUp,
  Clock,
  Activity,
  ChartSpline,
  type LucideIcon,
} from 'lucide-react';
import { useStatsDisplay } from '@/features/Progress';
import { useClick } from '@/shared/hooks/generic/useAudio';
import * as DialogPrimitive from '@radix-ui/react-dialog';

// Toggle between old full-screen layout (false) and new modal layout (true)
const USE_STATS_MODAL_LAYOUT = false;

const sessionStatIconBadgeStyle = {
  base: 'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border-b-4 [--float-distance:-0px]',
  selected:
    'border-(--main-color-accent) bg-(--main-color) text-(--background-color)',
  unselected:
    'border-(--main-color-accent) bg-(--main-color) text-(--background-color) opacity-85',
} as const;

interface BentoTileProps {
  Icon: LucideIcon;
  label: string;
  value: string | number;
  description?: string;
  className?: string;
  valueClassName?: string;
}

function BentoTile({
  Icon,
  label,
  value,
  description,
  className = '',
  valueClassName = '',
}: BentoTileProps) {
  return (
    <div
      className={`flex min-w-0 flex-col justify-between overflow-hidden rounded-[2rem] border-2 border-(--secondary-color)/10 bg-(--background-color) p-4 sm:p-5 ${className}`}
    >
      <div className='mb-2 flex min-w-0 items-center gap-2'>
        <span
          className={`${sessionStatIconBadgeStyle.base} ${sessionStatIconBadgeStyle.unselected}`}
        >
          <Icon className='h-5 w-5' />
        </span>
        <span className='block min-w-0 text-[11px] leading-tight font-bold tracking-wider break-words text-(--secondary-color) uppercase opacity-60 sm:text-xs'>
          {label}
        </span>
      </div>
      <div
        className={`min-w-0 overflow-hidden text-2xl font-black tracking-tighter break-words text-(--main-color) sm:text-3xl lg:text-4xl ${valueClassName}`}
      >
        {value}
      </div>
      {description ? (
        <p className='mt-2 text-sm text-(--secondary-color) lowercase opacity-60'>
          {description}
        </p>
      ) : null}
    </div>
  );
}

const SessionStats: React.FC = () => {
  const { playClick } = useClick();
  const statsData = useStatsDisplay();
  const toggleStats = statsData.toggleStats;

  // Handle ESC key to close stats (only for fullscreen layout; Dialog handles ESC in modal mode)
  useEffect(() => {
    if (USE_STATS_MODAL_LAYOUT) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation(); // Prevent the event from bubbling to ReturnFromGame
        playClick();
        toggleStats();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [playClick, toggleStats]);

  // Get data from facade
  const numCorrectAnswers = statsData.correctAnswers;
  const numWrongAnswers = statsData.wrongAnswers;
  const characterHistory = statsData.characterHistory;
  const totalMilliseconds = statsData.totalMilliseconds;
  const correctAnswerTimes = statsData.correctAnswerTimes;

  // Memoized stat calculations
  const stats = useMemo(() => {
    // Calculate time
    const totalMinutes = Math.floor(totalMilliseconds / 60000);
    const seconds = Math.floor((totalMilliseconds / 1000) % 60);
    const timeDisplay = `${totalMinutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    // Calculate accuracy metrics
    const totalAnswers = numCorrectAnswers + numWrongAnswers;
    const accuracy =
      totalAnswers > 0 ? (numCorrectAnswers / totalAnswers) * 100 : 0;
    const ciRatio =
      numWrongAnswers > 0
        ? numCorrectAnswers / numWrongAnswers
        : numCorrectAnswers > 0
          ? Infinity
          : 0;

    // Calculate timing metrics
    const hasAnswers = correctAnswerTimes.length > 0;
    const avgTime = hasAnswers
      ? (
          correctAnswerTimes.reduce((sum, t) => sum + t, 0) /
          correctAnswerTimes.length
        ).toFixed(2)
      : null;
    const fastestTime = hasAnswers
      ? Math.min(...correctAnswerTimes).toFixed(2)
      : null;
    const slowestTime = hasAnswers
      ? Math.max(...correctAnswerTimes).toFixed(2)
      : null;

    const uniqueChars = [...new Set(characterHistory)].length;

    return {
      totalMinutes,
      seconds,
      timeDisplay,
      totalAnswers,
      accuracy,
      ciRatio,
      hasAnswers,
      avgTime,
      fastestTime,
      slowestTime,
      uniqueChars,
    };
  }, [
    totalMilliseconds,
    numCorrectAnswers,
    numWrongAnswers,
    correctAnswerTimes,
    characterHistory,
  ]);

  const formatValue = (
    value: string | number | null | undefined,
    suffix: string = '',
  ): string => {
    if (value === null || value === undefined) return '~';
    if (value === Infinity) return '∞';
    return `${value}${suffix}`;
  };

  const statsContent = (
    <>
      <div className='mb-6 flex flex-col items-center gap-1 text-center select-none sm:mb-8 sm:items-start sm:text-left lg:mb-10'>
        <h1 className='text-3xl font-black tracking-tighter text-(--main-color) lowercase sm:text-4xl lg:text-5xl'>
          statistics
        </h1>
        <p className='text-sm font-medium tracking-tight text-(--secondary-color) lowercase opacity-60 sm:text-base lg:text-lg'>
          track your performance in the current session.
        </p>
      </div>

      <div className='mb-6 flex flex-col gap-4 sm:mb-8 sm:gap-5 lg:mb-10'>
        <div className='grid auto-rows-[minmax(120px,auto)] grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5 lg:grid-cols-6'>
          <div className='relative flex flex-col items-center justify-center rounded-[2.5rem] border-2 border-(--main-color)/20 bg-(--background-color) p-5 sm:col-span-2 sm:flex-row sm:gap-8 sm:p-6 lg:col-span-3 lg:row-span-2'>
            <div className='relative flex aspect-square w-full max-w-28 flex-col items-center justify-center sm:max-w-36'>
              <div
                className='h-full w-full rounded-full'
                style={{
                  background: `conic-gradient(var(--main-color) 0deg ${stats.accuracy * 3.6}deg, var(--border-color) ${stats.accuracy * 3.6}deg 360deg)`,
                }}
              />
              <div className='absolute inset-[12%] rounded-full bg-(--background-color)' />
              <div className='absolute inset-0 flex flex-col items-center justify-center'>
                <span className='text-4xl font-black tracking-tighter text-(--main-color) sm:text-5xl'>
                  {Math.round(stats.accuracy)}%
                </span>
              </div>
            </div>

            <div className='mt-6 flex flex-col items-center text-center sm:mt-0 sm:items-start sm:text-left'>
              <div className='mb-1 flex items-center gap-2'>
                <Target className='h-5 w-5 text-(--main-color)' />
                <span className='text-sm leading-none font-bold tracking-wider text-(--secondary-color) uppercase opacity-60'>
                  accuracy
                </span>
              </div>
              <div className='text-3xl font-black tracking-tighter text-(--main-color) sm:text-5xl'>
                {formatValue(numCorrectAnswers)} /{' '}
                {formatValue(stats.totalAnswers)}
              </div>
              <p className='mt-2 text-sm text-(--secondary-color) lowercase opacity-60 sm:text-base'>
                out of {stats.totalAnswers} attempts, you answered{' '}
                {numCorrectAnswers} correctly.
              </p>
            </div>
          </div>

          <BentoTile
            Icon={Hourglass}
            label='training time'
            value={stats.timeDisplay}
            className='lg:col-span-2'
            valueClassName='text-4xl sm:text-5xl'
          />
          <BentoTile
            Icon={Activity}
            label='answers'
            value={stats.totalAnswers}
            className='lg:col-span-1'
            valueClassName='text-4xl sm:text-5xl'
          />
          <BentoTile
            Icon={Check}
            label='correct'
            value={numCorrectAnswers}
            className='lg:col-span-1'
          />
          <BentoTile
            Icon={X}
            label='wrong'
            value={numWrongAnswers}
            className='lg:col-span-1'
          />
          <BentoTile
            Icon={TrendingUp}
            label='correct/incorrect'
            value={formatValue(
              stats.ciRatio === Infinity ? '∞' : stats.ciRatio.toFixed(2),
            )}
            className='lg:col-span-1'
            valueClassName='break-all text-xl sm:text-2xl'
          />
          <BentoTile
            Icon={Shapes}
            label='unique chars'
            value={stats.uniqueChars}
            className='lg:col-span-2'
          />
          <BentoTile
            Icon={Timer}
            label='average time'
            value={formatValue(stats.avgTime, 's')}
            className='lg:col-span-2'
          />
          <BentoTile
            Icon={Flame}
            label='fastest answer'
            value={formatValue(stats.fastestTime, 's')}
            className='lg:col-span-1'
          />
          <BentoTile
            Icon={Clock}
            label='slowest answer'
            value={formatValue(stats.slowestTime, 's')}
            className='lg:col-span-1'
          />
        </div>
      </div>

      <div className='sticky bottom-0 z-10 -mx-4 mt-auto flex w-auto items-center justify-center gap-3 border-t-2 border-(--border-color) bg-(--background-color) px-4 py-4 select-none sm:static sm:mx-0 sm:w-full sm:justify-start sm:gap-5 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0'>
        <button
          onClick={() => {
            playClick();
            toggleStats();
          }}
          className='group flex h-14 flex-1 cursor-pointer items-center justify-center gap-3 rounded-xl bg-(--secondary-color) px-4 text-lg font-bold text-(--background-color) lowercase outline-hidden transition-all duration-150 sm:px-10 sm:text-xl md:flex-none'
        >
          <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-(--background-color) bg-(--background-color) text-(--secondary-color)'>
            <ArrowLeft
              className='h-5 w-5 group-hover:animate-none sm:h-6 sm:w-6'
              strokeWidth={2.5}
            />
          </span>
          <span className='leading-none'>back</span>
        </button>
      </div>
    </>
  );

  if (USE_STATS_MODAL_LAYOUT) {
    return (
      <DialogPrimitive.Root open={true} onOpenChange={() => toggleStats()}>
        <DialogPrimitive.Portal forceMount>
          <DialogPrimitive.Overlay className='fixed inset-0 z-50 bg-black/80' />
          <DialogPrimitive.Content
            className='fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-[95vw] max-w-7xl -translate-x-1/2 -translate-y-1/2 flex-col gap-0 rounded-2xl border-0 border-(--border-color) bg-(--background-color) p-0 sm:max-h-[85vh] sm:w-[90vw]'
            onOpenAutoFocus={e => e.preventDefault()}
          >
            <div className='sticky top-0 z-10 flex flex-row items-center justify-between rounded-t-2xl border-b border-(--border-color) bg-(--background-color) px-6 pt-6 pb-4'>
              <DialogPrimitive.Title className='flex items-center gap-2 text-2xl font-semibold text-(--main-color)'>
                <span className='motion-safe:animate-float flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border-b-6 border-(--secondary-color-accent) bg-(--secondary-color) leading-none text-(--background-color) [--float-distance:-4px]'>
                  <ChartSpline size={22} />
                </span>
                statistics
              </DialogPrimitive.Title>
              <button
                onClick={() => {
                  playClick();
                  toggleStats();
                }}
                className='shrink-0 rounded-xl p-2 hover:cursor-pointer hover:bg-(--card-color)'
              >
                <X size={24} className='text-(--secondary-color)' />
              </button>
            </div>
            <div id='modal-scroll' className='flex-1 overflow-y-auto px-6 py-6'>
              {statsContent}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <div className='fixed inset-0 z-50 flex h-full w-full flex-col overflow-x-hidden overflow-y-auto bg-(--background-color)'>
      <div className='mx-auto my-auto flex min-h-full w-full max-w-7xl flex-1 flex-col justify-start px-4 py-6 sm:px-8 sm:py-10 lg:px-12 lg:py-12'>
        {statsContent}
      </div>
    </div>
  );
};

export default SessionStats;
